import { page } from '$app/stores';
import { derived, get, type Readable } from 'svelte/store';

import { region, type Region } from '$lib/region';

import type {
  Filter,
  PriceRangeFilter,
  ProductCollectionSortKeys,
  SearchSortKeys
} from '$gql/storefront/schemas';
import { getCurrentPath } from '$lib/utils/paths';

// A lot of blocks/sections will use category cross-selling which will require
// the cover image, since the first component to need them is the navbar it makes
// sense to fetch them on the layout and use them in a store and then not have to
// resolve relations every time
export const categories = derived(page, ($page) => $page.data.categories);

// Sitewide sale parse
export const sitewideSale = derived([page, region], ([$page, $region]) => {
  const {
    sws_label: label,
    sws_price_label: price_label,
    sws_excluded_tags: excluded_tags,
    sws_us_discount: us_discount,
    sws_eu_discount: eu_discount,
    sws_uk_discount: uk_discount,
    sws_active: active
  } = $page.data.configuration.content;

  const discounts: Record<Region, number> = {
    us: +us_discount / 100,
    eu: +eu_discount / 100,
    uk: +uk_discount / 100
  };

  if (!active) return;

  return {
    label,
    price_label,
    exclusions: (excluded_tags || '').split('\n'),
    getDiscountedPrice: (price: number) => Math.ceil(price * (1 - discounts[$region]))
  };
});

// Filters
export const filterConfiguration = derived(page, ($page) => {
  return {
    sort_options: $page.data.configuration.content.filter_sort_options,
    search_sort_options: $page.data.configuration.content.search_sort_options,
    price_ranges: $page.data.configuration.content.filter_price_ranges,
    filters_with_asset: $page.data.configuration.content.filter_filters_with_asset,
    top_searches: $page.data.configuration.content.search_top_searches
  };
});

export type StoryblokSortKey = {
  label: string;
  key: ProductCollectionSortKeys | SearchSortKeys;
  reverse: boolean;
};

export interface FilterStoryblokSortKey extends StoryblokSortKey {
  regex_type?: '' | 'exclude' | 'include';
  regex_content?: string | string[];
  hideFilter: boolean;
}

// Sort options
export const filterConfigurationSortKeys: Readable<FilterStoryblokSortKey[]> = derived(
  [filterConfiguration, page],
  ([$config, $page]) => {
    return $config.sort_options.map(({ label, type, reverse, regex_type, regex_content }) => {
      const blacklist =
        regex_content && typeof regex_content === 'string' ? regex_content.split('\n') : [];

      const validatePath = (pathname: string) => {
        const filter = regex_type || 'exclude';
        if (filter === 'exclude') {
          return blacklist.includes(pathname);
        }
        if (filter === 'include') {
          return !blacklist.includes(pathname);
        }
        return false;
      };

      const hideFilter = validatePath('/' + getCurrentPath($page.params));

      return {
        label,
        key: type as ProductCollectionSortKeys,
        reverse: !!reverse,
        regex_type,
        regex_content,
        hideFilter
      };
    });
  }
);

export const filterConfigurationSearchSortKeys: Readable<StoryblokSortKey[]> = derived(
  filterConfiguration,
  ($config) =>
    $config.search_sort_options.map(({ label, type, reverse }) => ({
      label,
      key: type as SearchSortKeys,
      reverse: !!reverse
    }))
);

// Price ranges
export const filterConfigurationPriceRanges: Readable<PriceRangeFilter[]> = derived(
  [filterConfiguration, region],
  ([$config, $region]) => {
    const filteredRanges = $config.price_ranges.filter((range) => range.regions.includes($region));
    return filteredRanges.map(({ from, to }) => {
      if (from && to && +to > +from) {
        return { min: +from, max: +to };
      } else if (from && !to) {
        return { min: +from, max: undefined };
      } else if (!from && to) {
        return { min: undefined, max: +to };
      } else return { min: undefined, max: undefined };
    });
  }
);

// Filters with asset
export const parseProductVariantName = (optionName: string) => {
  return optionName.split(',').map((name) => name.trim());
};

export function getConfigFilterField(optionName: string, optionValue: string) {
  const productVariants = get(filterConfiguration).filters_with_asset;
  if (!productVariants) return;

  const filterGroup = productVariants.find((variantGroup) =>
    parseProductVariantName(variantGroup.name).includes(optionName)
  );
  if (!filterGroup) return;

  const filter = [...filterGroup.filters].find((filter) => {
    return filter.name === optionValue;
  });

  if (filter) return { name: filter.name, image: filter.image };
}

export function resolveShopFilterOptions(filter: Filter) {
  return {
    ...filter,
    values: filter.values
      .map((filterValue) => {
        return {
          ...filterValue,
          relation: getConfigFilterField(filter.label, filterValue.label)
        };
      })
      .sort((a, b) => {
        return typeof a.relation === 'object' && !b.relation ? -1 : 1;
      })
  };
}
